import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
import { MdArrowBack } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { useProducts } from '../context/ProductsContext';
import './Products.css';

const SearchResults = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const { addToWishlist, removeFromWishlist, isInWishlist, addToCart } = useAuth();
    const { searchProducts, loading: productsLoading } = useProducts();

    const results = useMemo(() => {
        if (!query.trim()) return [];

        return searchProducts(query).map((product) => {
            const originalPrice = parseInt(product.originalPrice || 0);
            const discountedPrice = product.discountedPrice && product.discountedPrice !== ''
                ? parseInt(product.discountedPrice)
                : originalPrice;
            const discount = parseInt(product.discount) || 0;
            const productImages = Array.isArray(product.images) ? product.images : [];
            const primaryImage =
                productImages.find((img) => !/\.heic$/i.test(img)) ||
                productImages[0] ||
                '/No-Image-Placeholder.svg';

            return {
                ...product,
                id: product.productId,
                name: product.productTitle,
                originalPrice,
                discountedPrice,
                image: primaryImage,
                discount,
                inStock: product.inStock
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, searchProducts]);

    const toggleWishlist = (product) => {
        if (isInWishlist(product.id)) {
            removeFromWishlist(product.id);
        } else {
            addToWishlist({
                id: product.id,
                title: product.name,
                name: product.name,
                originalPrice: product.originalPrice,
                discountedPrice: product.discountedPrice,
                discount: product.discount,
                image: product.image,
                inStock: true,
                category: product.category
            });
        }
    };

    return (
        <div className="products-page">
            <button type="button" className="back-btn" onClick={() => navigate(-1)}>
                <MdArrowBack size={20} />
                Back
            </button>

            <div className="products-header">
                <h1>Search Results</h1>
                <p>{results.length} products found for "{query}"</p>
            </div>

            <div className="products-grid">
                {results.map((product) => {
                    const inWishlist = isInWishlist(product.id);

                    return (
                        <div key={product.id} className="product-card">
                            {product.discount > 0 && (
                                <span className="discount-badge">{product.discount}%</span>
                            )}
                            <button
                                type="button"
                                className="wishlist-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleWishlist(product);
                                }}
                                aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                            >
                                {inWishlist ? (
                                    <AiFillHeart size={24} color="#e63946" />
                                ) : (
                                    <AiOutlineHeart size={24} />
                                )}
                            </button>

                            <div
                                className="product-clickable"
                                onClick={() => navigate(`/product/${product.id}`, { state: { product } })}
                                style={{ cursor: 'pointer' }}
                            >
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className="product-image"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = '/No-Image-Placeholder.svg';
                                    }}
                                />

                                <div className="product-info">
                                    <h3>{product.name}</h3>
                                    <div className="product-price">
                                        {product.discount > 0 ? (
                                            <>
                                                <span className="original-price">₹{product.originalPrice}</span>
                                                <span className="current-price">₹{product.discountedPrice}</span>
                                            </>
                                        ) : (
                                            <span className="current-price">₹{product.originalPrice}</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="add-to-cart-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    addToCart({
                                        id: product.id,
                                        title: product.name,
                                        name: product.name,
                                        price: product.discountedPrice,
                                        image: product.image,
                                        quantity: 1,
                                        inStock: product.inStock
                                    });
                                    alert('Added to cart!');
                                }}
                            >
                                Add to Cart
                            </button>
                        </div>
                    );
                })}
            </div>

            {results.length === 0 && !productsLoading && (
                <div className="no-products">
                    <p>{query.trim() ? `No products found for "${query}".` : 'Type something in the search bar to find products.'}</p>
                    <button type="button" onClick={() => navigate('/')}>Browse All Products</button>
                </div>
            )}
        </div>
    );
};

export default SearchResults;
